import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { debounce } from "lodash";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import { ModuleFilter } from "../../../../components/utils/ModuleFilter";
import {
  ArrowUpCircleIcon,
  CheckBadgeIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { updateUrlWithException } from "../../../../components/Utils";
import { getDeviationsAsync } from "../../../../slices/quality/deviation";
import DeviationNewDialog from "../../../../components/dashboard/quality/deviations/deviationNewDialog";
import {
  DEVIATION_LIST_FIELDS,
  DeviationsTableNewView,
} from "../../../../components/dashboard/quality/deviations/deviationsTableNewView";
import {
  deviationStatusOptions,
  getDeviationTypeOptions,
  priorityOptions,
} from "../../../../components/dashboard/quality/deviations/utils";
import { fetchUsers } from "../../../../api/user";

export const DeviationsList = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [searchParams] = useSearchParams();

  const skipFetchRef = useRef(false);
  const [controller, setController] = useState({
    filters: [{ name: "status", value: JSON.stringify(["1", "2"]) }],
    page: searchParams.get("page") ? searchParams.get("page") : 1,
    per_page: searchParams.get("per_page") ? searchParams.get("per_page") : 25,
    query: searchParams.get("query") ? searchParams.get("query") : "",
    sort: searchParams.get("sort") ? searchParams.get("sort") : "desc",
    sortBy: searchParams.get("sortBy")
      ? searchParams.get("sortBy")
      : "created_at",
    view: searchParams.get("view") ? searchParams.get("view") : "all",
    fields: DEVIATION_LIST_FIELDS,
  });

  const deviationState = useSelector((store) => {
    return store.deviations;
  });

  // Update all params from controller except filters (dont't touch filters)
  useEffect(() => {
    updateUrlWithException(
      // These will be updated
      {
        page: controller.page,
        per_page: controller.per_page,
        query: controller.query,
        sort: controller.sort,
        sortBy: controller.sortBy,
        view: controller.view,
      },
      // This will be preserved
      "filters"
    );
  }, [controller]);

  const { isLoading, totalCount, newCount } = deviationState;
  const [userOptions, setUserOptions] = useState([]);
  const [deviationTypeOptions, setDeviationTypeOptions] = useState([]);

  async function fetchAndMapUsers() {
    const controller = {
      filters: [{ name: "role", value: "deviations" }],
      page: 1,
      per_page: 100,
      query: "",
      sort: "asc",
      sortBy: "name",
      view: "all",
    };

    await fetchUsers(controller)
      .then((res) => {
        if (res.data) {
          const array = [];
          if (!res.data) return array;
          res.data.forEach((row) => {
            array.push({
              value: row.id?.toString(),
              label: row.displayTitle || row.name,
            });
          });
          setUserOptions(array);
        }
      })
      .catch((err) => {
        console.error(err);
      });
  }

  useEffect(() => {
    fetchAndMapUsers();
    getDeviationTypeOptions().then((options) => {
      setDeviationTypeOptions(
        (options || []).map((option) => ({
          value: String(option.value),
          label: option.title,
        }))
      );
    });
  }, []);

  const handlePageChange = (newPage) => {
    setController({
      ...controller,
      page: newPage,
    });
  };

  const handleFiltersChange = (filters) => {
    const backendFilters = [];

    if (filters.deviationStatuses?.length > 0) {
      backendFilters.push({
        name: "status",
        value: JSON.stringify(filters.deviationStatuses),
      });
    }

    if (filters.users?.length > 0) {
      backendFilters.push({
        name: "responsible_user",
        value: JSON.stringify(filters.users),
      });
    }

    if (filters.deviationTypes?.length > 0) {
      backendFilters.push({
        name: "deviation_type",
        value: JSON.stringify(filters.deviationTypes),
      });
    }

    if (filters.priorities?.length > 0) {
      backendFilters.push({
        name: "priority",
        value: JSON.stringify(filters.priorities),
      });
    }

    if (filters.onlyLinkedWorkOrders) {
      backendFilters.push({
        name: "work_order",
        value: true,
      });
    }

    skipFetchRef.current = false;
    setController({
      ...controller,
      page: 1,
      query: filters.query || "",
      filters: backendFilters,
    });
  };

  const debouncedDispatchAction = debounce((controller) => {
    if (skipFetchRef.current) {
      skipFetchRef.current = false;
      return;
    }
    dispatch(getDeviationsAsync(controller));
  }, 250);

  useEffect(() => {
    // Trigger the debounced dispatch action
    debouncedDispatchAction(controller);

    // Clean the debounced function on component unmount.
    return () => {
      debouncedDispatchAction.cancel();
    };
  }, [controller, dispatch]);

  const getInitialFilters = () => {
    const initialFilters = {
      query: controller.query || "",
      deviationStatuses: [],
      users: [],
      deviationTypes: [],
      priorities: [],
      onlyLinkedWorkOrders: false,
    };

    if (controller.filters?.length > 0) {
      controller.filters.forEach((filter) => {
        try {
          const values = JSON.parse(filter.value);
          switch (filter.name) {
            case "status":
              initialFilters.deviationStatuses = Array.isArray(values)
                ? values.map(String)
                : [String(values)];
              break;
            case "responsible_user":
              initialFilters.users = Array.isArray(values) ? values : [values];
              break;
            case "deviation_type":
              initialFilters.deviationTypes = Array.isArray(values)
                ? values.map(String)
                : [String(values)];
              break;
            case "priority":
              initialFilters.priorities = Array.isArray(values)
                ? values.map(String)
                : [String(values)];
              break;
            case "work_order":
              initialFilters.onlyLinkedWorkOrders = true;
              break;
          }
        } catch (error) {
          if (filter.name === "work_order") {
            initialFilters.onlyLinkedWorkOrders = true;
          }
        }
      });
    } else {
      initialFilters.deviationStatuses = ["1", "2"];
    }

    return initialFilters;
  };

  const [deviationNewDialogOpen, setDeviationNewDialogOpen] = useState(false);

  const headerMeta = [
    {
      label: "Total: " + totalCount,
      description: "",
      path: null,
      action: null,
      icon: CheckBadgeIcon,
      textColor: "text-gray-400",
      iconColor: "text-gray-400",
      hidden: false,
    },

    {
      label: "New: " + newCount,
      description: "",
      path: null,
      action: null,
      icon: ArrowUpCircleIcon,
      textColor: "text-gray-400",
      iconColor: "text-green-500",
      hidden: false,
    },
  ];

  return (
    <>
      <DashboardHeader
        subtitle=""
        actions={[]}
        title={"Deviations"}
        meta={headerMeta}
        mainButton={{
          label: "New",
          action: () => setDeviationNewDialogOpen(true),
          path: null,
          color: "",
          icon: PlusIcon,
        }}
      >
        <div className="mx-auto mt-8 grid min-w-0 max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
          <div className="min-w-0 space-y-6 rounded-lg bg-white px-4 py-6 sm:px-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
            <ModuleFilter
              disabled={isLoading}
              onFiltersChange={handleFiltersChange}
              initialFilters={getInitialFilters()}
              searchPlaceholder="Søk i avvik"
              filterTitle="Filtrer avvik"
              hideStatusFilter={true}
              userOptions={userOptions}
              customDropdownFilters={[
                {
                  key: "deviationStatuses",
                  label: t("Statuses"),
                  options: deviationStatusOptions.map((status) => ({
                    value: String(status.value),
                    label: status.label,
                  })),
                },
                {
                  key: "deviationTypes",
                  label: t("Deviation Types"),
                  options: deviationTypeOptions,
                },
                {
                  key: "priorities",
                  label: t("Priorities"),
                  options: priorityOptions.map((priority) => ({
                    value: String(priority.value),
                    label: priority.label,
                  })),
                },
              ]}
              customCheckboxButtonFilters={[
                {
                  key: "onlyLinkedWorkOrders",
                  label: "Kun knyttet arbeidsordre",
                  defaultValue: false,
                },
              ]}
            />
          </div>

          <div className="min-w-0 space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
            <DeviationsTableNewView
              itemState={deviationState}
              itemsCount={deviationState.totalCount}
              totalCount={deviationState.totalCount}
              currentCount={deviationState.currentCount}
              page={controller.page}
              onPageChange={handlePageChange}
              perPage={controller.per_page}
            />
          </div>
        </div>
      </DashboardHeader>

      <DeviationNewDialog
        open={deviationNewDialogOpen}
        onClose={() => setDeviationNewDialogOpen(false)}
        selected={null}
        // onRefresh={() => getReport()}
      />
    </>
  );
};
